import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  CircleAlert,
  Info,
  LoaderCircle,
  X,
} from "lucide-react";
import { NumericFormat } from "react-number-format";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import BudgetRecommendationInfo from "./BudgetRecommendationInfo";

type BulkBudgetRecommendationStatus =
  | "pending"
  | "processing"
  | "success"
  | "error";

export type BulkBudgetRecommendationLocation = {
  id: string | number;
  locationId?: string | number;
  name: string;
  status: BulkBudgetRecommendationStatus;
  recommendation?: {
    finalRecommendation?: string | number | null;
    [key: string]: unknown;
  } | null;
  error?: string | null;
  marketData?: Record<string, unknown>;
};

type BulkBudgetRecommendationProgressProps = {
  open: boolean;
  minimized?: boolean;
  completed: number;
  total: number;
  locations: BulkBudgetRecommendationLocation[];
  onCancel: () => void;
  onBudgetChange?: (_locationId: string | number, _budget?: number) => void;
  onClose?: () => void;
  onRetry?: (_locationId: string | number) => void;
  onToggleMinimized: () => void;
  removedLocationCount?: number;
};

type BudgetDraft = {
  value: string;
  floatValue?: number;
};

type BudgetConstraint = {
  minBudget?: number;
  maxBudget?: number;
};

type PendingStopAction = "cancel" | "close";

const REMOVED_LOCATION_NOTICE_AUTO_HIDE_MS = 5000;

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function getNumericConstraint(value: unknown) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim().replace(/[$,]/g, "");
  if (!normalizedValue) {
    return undefined;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

function getRecommendationBudgetConstraint(
  recommendation: BulkBudgetRecommendationLocation["recommendation"]
): BudgetConstraint | null {
  const trace = getRecord(recommendation?.trace);
  const inputCampaign = getRecord(getRecord(trace.inputs).campaign);
  const configCampaign = getRecord(getRecord(trace.config).campaign);
  const minBudget = getNumericConstraint(
    inputCampaign.minBudget ?? configCampaign.minBudget
  );
  const maxBudget = getNumericConstraint(
    inputCampaign.maxBudget ?? configCampaign.maxBudget
  );

  return minBudget === undefined && maxBudget === undefined
    ? null
    : { minBudget, maxBudget };
}

function getBudgetConstraintError(
  location: BulkBudgetRecommendationLocation,
  budget?: number
) {
  if (budget === undefined) {
    return "";
  }

  const constraint = getRecommendationBudgetConstraint(location.recommendation);
  if (!constraint) {
    return "";
  }

  const isBelowMinimum =
    constraint.minBudget !== undefined && budget < constraint.minBudget;
  const isAboveMaximum =
    constraint.maxBudget !== undefined && budget > constraint.maxBudget;

  if (!isBelowMinimum && !isAboveMaximum) {
    return "";
  }

  if (
    constraint.minBudget !== undefined &&
    constraint.maxBudget !== undefined
  ) {
    return `Please enter a budget between $${constraint.minBudget} and $${constraint.maxBudget}`;
  }

  if (constraint.minBudget !== undefined) {
    return `We recommend the budget at least $${constraint.minBudget}`;
  }

  return `We recommend the budget up to $${constraint.maxBudget}`;
}

function getStatusIcon(location: BulkBudgetRecommendationLocation) {
  if (location.status === "success") {
    return <CheckCircle2 aria-label="Loaded" className="status-success" />;
  }

  if (location.status === "error") {
    return <CircleAlert aria-label="Error" className="status-error" />;
  }

  if (location.status === "processing") {
    return <LoaderCircle aria-label="In progress" className="status-loading" />;
  }

  return <Circle aria-label="Pending" className="status-pending" />;
}

function getStatusLabel(location: BulkBudgetRecommendationLocation) {
  if (location.status === "success") {
    return "";
  }

  if (location.status === "error") {
    return "Failed";
  }

  if (location.status === "processing") {
    return "In Progress...";
  }

  return "Pending";
}

export default function BulkBudgetRecommendationProgress({
  open,
  minimized = false,
  completed,
  total,
  locations,
  onCancel,
  onBudgetChange,
  onClose,
  onRetry,
  onToggleMinimized,
  removedLocationCount = 0,
}: BulkBudgetRecommendationProgressProps) {
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, BudgetDraft>>(
    {}
  );
  const [budgetDraftErrors, setBudgetDraftErrors] = useState<
    Record<string, string>
  >({});
  const [dismissedRemovedLocationCount, setDismissedRemovedLocationCount] =
    useState(0);
  const [pendingStopAction, setPendingStopAction] =
    useState<PendingStopAction | null>(null);
  const shouldCloseAfterRemovedLocationNoticeDismiss =
    locations.length === 0 && removedLocationCount > 0;
  const hasInProgressLocations = locations.some(
    (location) => !["success", "error"].includes(location.status)
  );

  const locationKeySet = useMemo(
    () => new Set(locations.map((location) => String(location.id))),
    [locations]
  );

  const getBudgetValue = useCallback(
    (location: BulkBudgetRecommendationLocation) => {
      const locationKey = String(location.id);
      return (
        budgetDrafts[locationKey]?.value ??
        location.recommendation?.finalRecommendation ??
        ""
      );
    },
    [budgetDrafts]
  );

  const updateBudgetDraft = useCallback(
    (
      location: BulkBudgetRecommendationLocation,
      value: string,
      floatValue?: number
    ) => {
      const locationKey = String(location.id);
      const draftError = getBudgetConstraintError(location, floatValue);
      setBudgetDrafts((currentDrafts) => ({
        ...currentDrafts,
        [locationKey]: {
          value,
          floatValue,
        },
      }));
      if (!draftError) {
        setBudgetDraftErrors((currentErrors) => {
          if (!currentErrors[locationKey]) {
            return currentErrors;
          }

          const nextErrors = { ...currentErrors };
          delete nextErrors[locationKey];
          return nextErrors;
        });
      }
    },
    []
  );

  const submitBudgetDraft = useCallback(
    (location: BulkBudgetRecommendationLocation) => {
      const locationKey = String(location.id);
      const draft = budgetDrafts[locationKey];

      if (draft) {
        const draftError = getBudgetConstraintError(location, draft.floatValue);
        if (draftError) {
          setBudgetDraftErrors((currentErrors) => ({
            ...currentErrors,
            [locationKey]: draftError,
          }));
          return;
        }

        onBudgetChange?.(location.id, draft.floatValue);
      }

      setBudgetDraftErrors((currentErrors) => {
        if (!currentErrors[locationKey]) {
          return currentErrors;
        }

        const nextErrors = { ...currentErrors };
        delete nextErrors[locationKey];
        return nextErrors;
      });
      setBudgetDrafts((currentDrafts) => {
        const nextDrafts = { ...currentDrafts };
        delete nextDrafts[locationKey];
        return nextDrafts;
      });
    },
    [budgetDrafts, onBudgetChange]
  );

  const handleBudgetKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLInputElement>,
      location: BulkBudgetRecommendationLocation
    ) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      submitBudgetDraft(location);
    },
    [submitBudgetDraft]
  );

  const dismissRemovedLocationNotice = useCallback(() => {
    setDismissedRemovedLocationCount(removedLocationCount);

    if (shouldCloseAfterRemovedLocationNoticeDismiss) {
      onClose?.();
    }
  }, [
    onClose,
    removedLocationCount,
    shouldCloseAfterRemovedLocationNoticeDismiss,
  ]);

  useEffect(() => {
    setBudgetDrafts((currentDrafts) => {
      const nextDrafts = Object.keys(currentDrafts).reduce<
        Record<string, BudgetDraft>
      >((drafts, locationKey) => {
        if (locationKeySet.has(locationKey)) {
          drafts[locationKey] = currentDrafts[locationKey];
        }
        return drafts;
      }, {});

      return Object.keys(nextDrafts).length ===
        Object.keys(currentDrafts).length
        ? currentDrafts
        : nextDrafts;
    });
    setBudgetDraftErrors((currentErrors) => {
      const nextErrors = Object.keys(currentErrors).reduce<
        Record<string, string>
      >((errors, locationKey) => {
        if (locationKeySet.has(locationKey)) {
          errors[locationKey] = currentErrors[locationKey];
        }
        return errors;
      }, {});

      return Object.keys(nextErrors).length ===
        Object.keys(currentErrors).length
        ? currentErrors
        : nextErrors;
    });
  }, [locationKeySet]);

  const showRemovedLocationNotice =
    removedLocationCount > dismissedRemovedLocationCount;

  useEffect(() => {
    if (removedLocationCount === 0 && dismissedRemovedLocationCount !== 0) {
      setDismissedRemovedLocationCount(0);
    }
  }, [dismissedRemovedLocationCount, removedLocationCount]);

  useEffect(() => {
    if (!showRemovedLocationNotice) {
      return undefined;
    }

    const timeoutId = setTimeout(
      dismissRemovedLocationNotice,
      REMOVED_LOCATION_NOTICE_AUTO_HIDE_MS
    );

    return () => {
      clearTimeout(timeoutId);
    };
  }, [dismissRemovedLocationNotice, showRemovedLocationNotice]);

  const requestStop = useCallback(
    (action: PendingStopAction) => {
      if (!hasInProgressLocations) {
        if (action === "cancel") {
          onCancel();
        } else {
          onClose?.();
        }
        return;
      }

      setPendingStopAction(action);
    },
    [hasInProgressLocations, onCancel, onClose]
  );

  const confirmStop = useCallback(() => {
    const action = pendingStopAction;
    setPendingStopAction(null);

    if (action === "cancel") {
      onCancel();
    } else if (action === "close") {
      onClose?.();
    }
  }, [onCancel, onClose, pendingStopAction]);

  if (!open) {
    return null;
  }

  const canCancel = hasInProgressLocations;
  const summaryText = canCancel
    ? `In Progress... ${completed} of ${total} locations complete`
    : `Completed ${completed} of ${total} locations`;

  return (
    <div className="bulk-budget-recommendation-progress" role="status">
      <div className="bulk-budget-recommendation-header">
        <BudgetRecommendationInfo
          recommendation={null}
          label="Recommended Budgets"
        />
        <div className="bulk-budget-recommendation-header-actions">
          <IconButton
            type="button"
            size="small"
            aria-label={
              minimized
                ? "Expand budget recommendation progress"
                : "Minimize budget recommendation progress"
            }
            onClick={onToggleMinimized}
          >
            {minimized ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </IconButton>
          <IconButton
            type="button"
            size="small"
            aria-label="Close budget recommendation progress"
            onClick={() => requestStop("close")}
          >
            <X size={16} />
          </IconButton>
        </div>
      </div>
      <Collapse in={!minimized} unmountOnExit>
        {showRemovedLocationNotice ? (
          <Alert
            className="bulk-budget-recommendation-removed-alert"
            severity="warning"
            icon={<Info size={16} />}
            action={
              <IconButton
                aria-label="Dismiss removed budget notice"
                color="inherit"
                size="small"
                onClick={dismissRemovedLocationNotice}
              >
                <X size={16} />
              </IconButton>
            }
          >
            Updated budget removed from the list.
          </Alert>
        ) : null}
        <div className="bulk-budget-recommendation-summary">
          <span>{summaryText}</span>
          {canCancel ? (
            <Button
              type="button"
              variant="outlined"
              onClick={() => requestStop("cancel")}
            >
              Cancel
            </Button>
          ) : null}
        </div>
        <div className="bulk-budget-recommendation-locations">
          {locations.map((location) => (
            <div
              className={`bulk-budget-recommendation-location is-${location.status}`}
              key={location.id}
            >
              <div
                className={`bulk-budget-recommendation-location-main${
                  getStatusLabel(location) ? " has-status" : ""
                }`}
                role="group"
                aria-label={`${location.name} budget recommendation status`}
              >
                <span className="bulk-budget-recommendation-location-icon">
                  {getStatusIcon(location)}
                </span>
                <div className="bulk-budget-recommendation-location-copy">
                  <strong className="bulk-budget-recommendation-location-name">
                    {location.name}
                  </strong>
                  {budgetDraftErrors[String(location.id)] ? (
                    <FormHelperText
                      className="bulk-budget-recommendation-budget-error"
                      error
                    >
                      {budgetDraftErrors[String(location.id)]}
                    </FormHelperText>
                  ) : null}
                </div>
                {getStatusLabel(location) ? (
                  <span className="bulk-budget-recommendation-location-status">
                    {getStatusLabel(location)}
                  </span>
                ) : null}
              </div>
              {location.status === "success" && location.recommendation ? (
                <div className="bulk-budget-recommendation-location-result">
                  <BudgetRecommendationInfo
                    recommendation={location.recommendation}
                    marketData={location.marketData}
                    showLabel={false}
                    infoAriaLabel={`View budget recommendation details for ${location.name}`}
                  />
                  <NumericFormat
                    className="bulk-budget-recommendation-budget-field"
                    customInput={TextField}
                    error={Boolean(budgetDraftErrors[String(location.id)])}
                    value={getBudgetValue(location)}
                    onValueChange={(values) =>
                      updateBudgetDraft(
                        location,
                        values.value,
                        values.floatValue
                      )
                    }
                    onBlur={() => submitBudgetDraft(location)}
                    thousandSeparator
                    decimalScale={2}
                    allowNegative={false}
                    size="small"
                    slotProps={{
                      input: {
                        className: "bulk-budget-recommendation-budget-input",
                        onKeyDown: (event) =>
                          handleBudgetKeyDown(event, location),
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon
                              component={RpSvgIcon}
                              name="money1"
                              color="primary"
                              style={{ color: "#0a58b5" }}
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              ) : null}
              {location.status === "error" ? (
                <div className="bulk-budget-recommendation-location-result">
                  <BudgetRecommendationInfo
                    recommendation={{
                      explanation:
                        location.error ||
                        "Budget recommendation failed for this location.",
                    }}
                    marketData={location.marketData}
                    showLabel={false}
                    infoAriaLabel={`View budget recommendation error for ${location.name}`}
                  />
                  {onRetry ? (
                    <Button
                      className="bulk-budget-recommendation-retry"
                      type="button"
                      variant="outlined"
                      aria-label={`Try again for ${location.name}`}
                      onClick={() => onRetry(location.id)}
                    >
                      Try Again
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </Collapse>
      {minimized ? (
        <div className="bulk-budget-recommendation-summary is-minimized">
          <span>{summaryText}</span>
          {canCancel ? (
            <>
              <Button
                type="button"
                variant="outlined"
                onClick={() => requestStop("cancel")}
              >
                Cancel
              </Button>
              <IconButton
                type="button"
                size="small"
                aria-label="Cancel budget recommendation progress"
                onClick={() => requestStop("cancel")}
              >
                <X size={16} />
              </IconButton>
            </>
          ) : null}
        </div>
      ) : null}
      <Dialog
        open={pendingStopAction !== null}
        onClose={() => setPendingStopAction(null)}
        aria-labelledby="stop-budget-recommendations-title"
        aria-describedby="stop-budget-recommendations-description"
      >
        <DialogTitle id="stop-budget-recommendations-title">
          Stop recommendations?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="stop-budget-recommendations-description"
            variant="body2"
            sx={{ color: "text.primary" }}
          >
            Stopping now will cancel recommendations for all locations that are
            still in progress.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="outlined"
            color="error"
            disableElevation
            onClick={confirmStop}
          >
            Stop recommendations
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disableElevation
            autoFocus
            onClick={() => setPendingStopAction(null)}
          >
            Keep running
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
